import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaPlus } from 'react-icons/fa';
import { 
  Box, 
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Switch
} from '@mui/material';
import * as d3 from 'd3';
import { organizationApi, employeesApi } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import { 
  ASSIGNMENT_MODEL_TYPES,
  getAssignmentModelTypeName,
  getAssignmentModelTypeBackgroundColor,
  getAssignmentModelTypeTextColor
} from '../../../../constants/assignmentModel';
import EmployeeSearch from '../../../common/EmployeeSearch';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Modal from '../../../common/Modal';
import { FormContainer } from '../../../ProjectsManagement/ProjectForm/styles';
import { Content } from '../../../ProjectsManagement/ProjectForm/styles';
import EmptyState from '../../../common/EmptyState';
import LoadingSpinner from '../../../common/LoadingSpinner';
import OrgUnitTypeSelector from '../../../common/OrgUnitTypeSelector';
import AssignmentModelSelector from '../../../common/AssignmentModelSelector';
import ManagerHistoryModal from '../../../common/ManagerHistoryModal';
import MoveBusinessUnitModal from './MoveBusinessUnitModal';
import EndBusinessUnitModal from './EndBusinessUnitModal';
import { MANAGER_HISTORY_ENTITY_TYPES } from '../../../../constants/managerHistory';
import { BU_SCOPE, scopeBusinessUnitTree, isRootSelectableBy } from '../../../../utils/businessUnitScope';
import { formatDateOnly } from '../../../../utils/dateOnly';

// ADR-125: ancestor-only nodes in the scoped tree, and the root for a non-owner.
const CONTEXT_NODE_OPACITY = 0.45;

const BusinessUnitsTab = ({
  clusterId,
  onSelectBusinessUnit,
  viewOnly,
  initialBusinessUnit,
  readOnly,
  onClusterManagerChange,
  hideInactiveToggle = false,
  // ADR-125 — scope + root-ownership inputs. All optional, and honoured ONLY when
  // `viewOnly` is true: the admin ClusterForm tree (no viewOnly) is never scoped.
  // A view filter, not authorization.
  myBusinessUnitIds = null,
  scope = BU_SCOPE.ALL,
  onScopeResolved,
  currentEmployeeId = null,
}) => {
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [businessUnits, setBusinessUnits] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, businessUnit: null, readOnly: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, businessUnit: null });
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [moveModal, setMoveModal] = useState({ open: false, businessUnit: null });
  // ADR-120: lifecycle dialog (end / reactivate) and the "show inactive" toggle
  const [lifecycleModal, setLifecycleModal] = useState({ open: false, mode: 'end', businessUnit: null });
  const [showInactive, setShowInactive] = useState(false);
  const [historyModal, setHistoryModal] = useState({ open: false, entityId: null, entityName: '', entityType: null, readOnly: false });
  const svgRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    ownerId: '',
    orgUnitTypeId: '',
    assignmentModel: ''
  });

  useEffect(() => {
    if (clusterId) {
      fetchBusinessUnits();
    }
  }, [clusterId, showInactive]);

  // ADR-125 Decision 4 — the raw payload in `businessUnits` stays untouched; the scoped
  // projection is derived. `mine` is computed whatever the active scope, because the parent
  // needs it (toggle count + Decision 6) while possibly showing ALL.
  const mineScope = useMemo(
    () => (viewOnly && businessUnits ? scopeBusinessUnitTree(businessUnits, myBusinessUnitIds) : null),
    [viewOnly, businessUnits, myBusinessUnitIds]
  );

  // MINE with no seed in this Cluster renders ALL here (no empty-tree frame); the parent
  // learns it from onScopeResolved and updates the toggle.
  const activeScope = useMemo(() => {
    if (viewOnly && scope === BU_SCOPE.MINE && mineScope?.selectableIds) return mineScope;
    return { tree: businessUnits, selectableIds: null, seedCount: 0 };
  }, [viewOnly, scope, mineScope, businessUnits]);

  // Read through a ref so a non-memoised caller cannot churn the effects below.
  const onScopeResolvedRef = useRef(onScopeResolved);
  useEffect(() => {
    onScopeResolvedRef.current = onScopeResolved;
  }, [onScopeResolved]);

  useEffect(() => {
    if (!mineScope || !onScopeResolvedRef.current) return;
    onScopeResolvedRef.current({
      selectableIds: mineScope.selectableIds,
      seedCount: mineScope.seedCount,
    });
  }, [mineScope]);

  const fetchBusinessUnits = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getBusinessUnitHierarchy(clusterId, { includeInactive: showInactive });
      setBusinessUnits(data[0]); // Assuming the first item is the root business unit
    } catch (err) {
      showAlert(err.message || 'Failed to fetch business units', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOwnerChange = (newOwner) => {
    setSelectedOwner(newOwner);
    setFormData(prev => ({
      ...prev,
      ownerId: newOwner?.id || ''
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ownerId: '',
      orgUnitTypeId: '',
      assignmentModel: ''
    });
    setSelectedOwner(null);
  };

  const handleAddBusinessUnit = async (e) => {
    e.preventDefault();
    const businessUnitData = {
      name: formData.name,
      ownerId: formData.ownerId || null,
      clusterId: clusterId,
      parentBuId: selectedBusinessUnit?.id || null,
      orgUnitTypeId: formData.orgUnitTypeId || null,
      assignmentModel: formData.assignmentModel || null
    };

    try {
      setIsLoading(true);
      await organizationApi.createBusinessUnit(businessUnitData);
      setOpenModal(false);
      setSelectedBusinessUnit(null);
      resetForm(); // Reset the form after successful creation
      showAlert('Business Unit added successfully', 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to add business unit.', 'error', err);
      console.error('Error adding business unit:', err);
    } finally {
      setIsLoading(false);
      await fetchBusinessUnits();
    }
  };

  const handleEditBusinessUnit = async (e) => {
    e.preventDefault();
    if (editModal.readOnly) return; // ADR-120
    const businessUnitData = {
      name: formData.name,
      ownerId: formData.ownerId || null,
      clusterId: clusterId,
      orgUnitTypeId: formData.orgUnitTypeId || null,
      assignmentModel: formData.assignmentModel || null
    };

    try {
      setIsLoading(true);
      await organizationApi.updateBusinessUnit(editModal.businessUnit.id, businessUnitData);
      setEditModal({ open: false, businessUnit: null });
      resetForm(); // Reset the form after successful update
      showAlert('Business Unit updated successfully', 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to update business unit.', 'error', err);
      console.error('Error updating business unit:', err);
    } finally {
      setIsLoading(false);
      await fetchBusinessUnits();
    }
  };

  const handleDeleteBusinessUnit = (businessUnit) => {
    setDeleteModal({ open: true, businessUnit });
  };

  const confirmDelete = async () => {
    const buToDelete = deleteModal.businessUnit;
    setDeleteModal({ open: false, businessUnit: null });
    
    if (!buToDelete?.id) {
      showAlert('Invalid business unit data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteBusinessUnit(buToDelete.id);
      showAlert('Business Unit deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting business unit:', err);
      showAlert(err.message || 'Failed to delete business unit.', 'error', err);
    } finally {
      setIsLoading(false);
      await fetchBusinessUnits();
    }
  };



  // Initialize form data when editing
  useEffect(() => {
    if (editModal.open && editModal.businessUnit) {
      setFormData({
        name: editModal.businessUnit.name || '',
        ownerId: editModal.businessUnit.owner?.id || '',
        orgUnitTypeId: editModal.businessUnit.orgUnitType?.id || '',
        assignmentModel: editModal.businessUnit.assignmentModel || ''
      });
      setSelectedOwner(editModal.businessUnit.owner || null);
    }
  }, [editModal.open, editModal.businessUnit]);

  // ADR-118: the Move dialog (scope switch, preview, transfer mode) lives in
  // MoveBusinessUnitModal; this tab only reacts to its outcome.
  const handleMoved = async (result, { kind, destinationName, error } = {}) => {
    if (error) {
      showAlert(error.message || 'Failed to move business unit', 'error', error);
      return;
    }
    if (kind === 'transfer') {
      showAlert(`Business Unit transferred to ${destinationName || 'another Cluster'}`, 'success');
    } else {
      showAlert('Business Unit moved successfully', 'success');
    }
    setMoveModal({ open: false, businessUnit: null });
    await fetchBusinessUnits();
  };

  // ADR-120: outcome of the End / Reactivate dialog
  const handleLifecycleDone = async (result, { mode, endDate, error } = {}) => {
    if (error) {
      showAlert(error.message || 'Failed to change the business unit lifecycle', 'error', error);
      return;
    }
    const name = lifecycleModal.businessUnit?.name || 'Business Unit';
    showAlert(mode === 'end' ? `${name} ends on ${formatDateOnly(endDate)}` : `${name} reactivated`, 'success');
    setLifecycleModal({ open: false, mode: 'end', businessUnit: null });
    await fetchBusinessUnits();
  };

  // First, fix the selection handler
  const handleBusinessUnitSelect = (businessUnit) => {
    if (viewOnly && onSelectBusinessUnit && !readOnly) {
      const isSelected = businessUnit.id === selectedBusinessUnit?.id;
      setSelectedBusinessUnit(isSelected ? null : businessUnit);
      if (onSelectBusinessUnit) {
        onSelectBusinessUnit(isSelected ? null : businessUnit);
      }
    }
  };

  const renderBusinessUnitForm = () => {
    return (
      <>
        <TextField
          fullWidth
          name="name"
          label="Business Unit Name"
          value={formData.name}
          onChange={handleFormChange('name')}
          required
          disabled={editModal.readOnly}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              height: '48px',
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#2563EB' }
            }
          }}
        />

        <Box sx={{ width: '100%', mb: 2 }}>
          <EmployeeSearch
            value={selectedOwner}
            onChange={handleOwnerChange}
            label="Owner"
            placeholder="No owner assigned"
            width={400}
            height="56px"
            sx={{ width: '100%' }}
            showSelectedAvatar={true}
            disabled={true}
          />
          <Typography variant="body2" sx={{ mt: 1, color: '#94A3B8', fontSize: '12px' }}>
            To assign or change the owner, use the history icon
            <Box component="span" sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: '#8B5CF6',
              color: 'white',
              fontSize: '9px',
              fontWeight: 'bold',
              lineHeight: 1,
              verticalAlign: 'middle',
              mx: '4px'
            }}>h</Box>
            on the business unit node in the tree.
          </Typography>
        </Box>

        <OrgUnitTypeSelector
          value={formData.orgUnitTypeId}
          onChange={handleFormChange('orgUnitTypeId')}
          label="Organizational Unit Type"
          required={false}
          disabled={editModal.readOnly}
          fullWidth
          sx={{ mb: 2 }}
        />

        <AssignmentModelSelector
          value={formData.assignmentModel}
          onChange={handleFormChange('assignmentModel')}
          label="Assignment Model"
          required={true}
          disabled={editModal.readOnly}
          fullWidth
          showDescription={true}
          showEmptyOption={false}
          sx={{ mb: 2 }}
        />
      </>
    );
  };

  useEffect(() => {
    // ADR-125: draw the active projection (the raw tree under ALL / outside the wizard).
    const treeData = activeScope.tree;
    const scopedSelectableIds = activeScope.selectableIds;
    if (!treeData || !svgRef.current) return;

    // Clear existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // Validate business unit data structure
    if (!treeData.name) {
      console.error('Invalid business unit data structure:', treeData);
      return;
    }

    // ADR-125 — which nodes the wizard may pick. Outside the wizard (no viewOnly) every
    // node stays selectable exactly as before.
    //   - scoped (MINE): only passes 1+2 of the scope; ancestors are context.
    //   - root (depth 0): only its owner, in both scopes (Decision 11).
    const isPickable = (d) => {
      if (!viewOnly) return true;
      if (d.depth === 0) return isRootSelectableBy(d.data, currentEmployeeId);
      return !scopedSelectableIds || scopedSelectableIds.has(d.data.id);
    };

    const margin = { top: 30, right: 40, bottom: 10, left: 40 };
    const width = 2000;
    const barHeight = 25;
    const treeIndent = 20;

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .style("font", "12px sans-serif");

    // Create main group for tree
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Transform business units data for D3
    const transformData = (bu) => ({
      id: bu.id,
      name: bu.name || 'Unnamed Business Unit',
      owner: bu.owner || null,
      clusterId: bu.clusterId || null,
      orgUnitType: bu.orgUnitType || null,
      assignmentModel: bu.assignmentModel || null,
      parentBuId: bu.parentBuId || null,
      // ADR-120: lifecycle fields (status is derived server-side)
      status: bu.status || 'Active',
      endDate: bu.endDate || null,
      endReason: bu.endReason || null,
      children: (bu.childBus || []).map(child => transformData(child))
    });

    // Create hierarchy
    const root = d3.hierarchy(transformData(treeData));
    root.descendants().forEach(d => {
      d._children = d.children;
      d.children = d._children;
    });

    const updateTree = (source) => {
      // Get visible nodes and links
      const nodes = root.descendants();
      const links = root.links();

      // Calculate height based on visible nodes
      const height = nodes.length * barHeight + margin.top + margin.bottom;

      // Update SVG height
      d3.select(svgRef.current)
        .attr("height", height);

      // Calculate positions
      let i = 0;
      root.eachBefore(d => {
        d.x = d.depth * treeIndent;
        d.y = i * barHeight;
        i++;
      });

      // Update links
      const link = g.selectAll(".link")
        .data(links, d => d.target.id || (d.target.id = Math.random()));

      const linkEnter = link.enter()
        .append("path")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#999")
        .attr("stroke-opacity", 0.4)
        .attr("d", d => {
          const o = { x: source.x0 || source.x, y: source.y0 || source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        });

      link.merge(linkEnter)
        .transition()
        .duration(300)
        .attr("d", d => {
          return `M${d.source.x},${d.source.y + barHeight / 2}
                  L${d.source.x},${d.target.y + barHeight / 2}
                  L${d.target.x},${d.target.y + barHeight / 2}`;
        });

      link.exit()
        .transition()
        .duration(300)
        .attr("d", d => {
          const o = { x: source.x, y: source.y };
          return `M${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}
                  L${o.x},${o.y + barHeight / 2}`;
        })
        .remove();

      // Update nodes
      const node = g.selectAll(".node")
        .data(nodes, d => d.id || (d.id = Math.random()));

      const nodeEnter = node.enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", d => `translate(0,${source.y0 || source.y})`)
        .style("opacity", 0);

      // Add expand/collapse button
      nodeEnter.append("g")
        .attr("class", "button-group")
        .attr("transform", d => `translate(${d.x - 15},${barHeight / 2})`)
        .style("cursor", d => d.depth === 0 ? "default" : "pointer")
        .style("display", d => d._children || d.children ? null : "none")
        .on("click", (event, d) => {
          event.stopPropagation();
          if (d.depth !== 0) {
            if (d.children) {
              d._children = d.children;
              d.children = null;
            } else {
              d.children = d._children;
              d._children = null;
            }
            updateTree(d);
          }
        })
        .each(function(d) {
          const group = d3.select(this);
          group.append("circle")
            .attr("r", 7)
            .attr("fill", "white")
            .attr("stroke", "#999");
          
          group.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("font-size", "10px")
            .text(d => d.children ? "−" : "+");
        });

      // Update the node group click handler
      const nodeGroup = nodeEnter.append("g")
        .attr("class", "node-group")
        //.style("cursor", viewOnly ? "default" : "pointer")
        .style("cursor", d => (readOnly || !isPickable(d)) ? "default" : "pointer")
        // ADR-125: context nodes (and the root for a non-owner) are drawn, not offered.
        // The current selection is never dimmed (e.g. a root picked by someone else — Decision 11).
        .style("opacity", d => (viewOnly && !readOnly && !isPickable(d) && d.data.id !== initialBusinessUnit?.id)
          ? CONTEXT_NODE_OPACITY : null)
        .on("click", function(event, d) {
          event.stopPropagation();
          
          if (readOnly) return; // Don't process clicks in readOnly mode
          if (!isPickable(d)) return; // ADR-125 invariants I6 / I12
          
          const allNodeGroups = g.selectAll(".node-group");
          const currentNode = d3.select(this);
          const wasSelected = currentNode.classed("selected");
          
          allNodeGroups.each(function() {
            const node = d3.select(this);
            node.classed("selected", false);
            node.select("text").style("font-weight", "normal");

            if (!viewOnly) {
              node.select(".add-subteam-button").style("opacity", 0);
              node.select(".edit-button").style("opacity", 0);
              node.select(".move-button").style("opacity", 0);
              node.select(".delete-button").style("opacity", 0);
              node.select(".history-button").style("opacity", 0);
              node.select(".end-button").style("opacity", 0); // ADR-120
              node.select(".reactivate-button").style("opacity", 0); // ADR-120
            }
          });

          if (!wasSelected) {
            currentNode.classed("selected", true);
            currentNode.select("text").style("font-weight", "bold");

            if (!viewOnly) {
              currentNode.select(".add-subteam-button").style("opacity", 1);
              currentNode.select(".edit-button").style("opacity", 1);
              currentNode.select(".move-button").style("opacity", 1);
              currentNode.select(".delete-button").style("opacity", 1);
              currentNode.select(".history-button").style("opacity", 1);
              currentNode.select(".end-button").style("opacity", 1); // ADR-120
              currentNode.select(".reactivate-button").style("opacity", 1); // ADR-120
            }
          }

          if (viewOnly && onSelectBusinessUnit && !readOnly) {
            handleBusinessUnitSelect(d.data);
          }
        });

      // Hide action buttons in view-only mode
      if (viewOnly || readOnly) {
        nodeGroup.selectAll(".add-subteam-button, .edit-button, .move-button, .delete-button, .history-button, .end-button, .reactivate-button")
          .style("display", "none");
      }

      // Add business unit content
      nodeGroup.each(function(d) {
        if (!d.data || !d.data.name) return; // Skip if data is invalid

        const node = d3.select(this);
        let totalWidth = d.x + 8;

        // Add business unit name
        const textElement = node.append("text")
          .attr("x", totalWidth)
          .attr("y", barHeight / 2)
          .attr("dy", "0.31em")
          .attr("text-anchor", "start")
          .attr("font-weight", d.data.id === initialBusinessUnit?.id ? "bold" : "normal")
          .attr("fill", d.data.status === 'Inactive' ? "#94A3B8" : null) // ADR-120
          .text(d.depth === 0 ? `Cluster: ${d.data.name}` : d.data.name);

        totalWidth += textElement.node().getComputedTextLength() + 16;

        // Add organizational unit type info if it exists
        if (d.data.orgUnitType?.name) {
          const typeGroup = node.append("g")
            .attr("transform", `translate(${totalWidth}, ${barHeight / 2})`);

          // Create the text element first to measure it
          const typeText = typeGroup.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "start")
            .attr("fill", "#6B7280")
            .attr("font-size", "11px")
            .text(d.data.orgUnitType.name);

          const textBBox = typeText.node().getBBox();
          const padding = 4;

          // Add background rectangle
          typeGroup.insert("rect", "text")
            .attr("x", -padding)
            .attr("y", -textBBox.height / 2 - padding / 2)
            .attr("width", textBBox.width + padding * 2)
            .attr("height", textBBox.height + padding)
            .attr("fill", "#F3F4F6")
            .attr("stroke", "#E5E7EB")
            .attr("stroke-width", 0.5)
            .attr("rx", 3);

          totalWidth += textBBox.width + padding * 2 + 16;
        }

        // ADR-120: lifecycle badge (status derived server-side)
        if (d.data.status && d.data.status !== 'Active') {
          const isEnding = d.data.status === 'Ending';
          const lifecycleGroup = node.append("g")
            .attr("class", "lifecycle-badge")
            .attr("transform", `translate(${totalWidth}, ${barHeight / 2})`);
          const lifecycleText = lifecycleGroup.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "start")
            .attr("fill", isEnding ? "#B45309" : "#6B7280")
            .attr("font-size", "11px")
            .text(isEnding ? `Ends ${formatDateOnly(d.data.endDate)}` : `Ended ${formatDateOnly(d.data.endDate)}`);
          const lifecycleBBox = lifecycleText.node().getBBox();
          lifecycleGroup.insert("rect", "text")
            .attr("x", -4)
            .attr("y", -lifecycleBBox.height / 2 - 2)
            .attr("width", lifecycleBBox.width + 8)
            .attr("height", lifecycleBBox.height + 4)
            .attr("fill", isEnding ? "#FEF3C7" : "#E5E7EB")
            .attr("stroke", isEnding ? "#FDE68A" : "#D1D5DB")
            .attr("stroke-width", 0.5)
            .attr("rx", 3);
          totalWidth += lifecycleBBox.width + 8 + 16;
        }

        // Add owner info only if it exists and has a fullName
        if (d.data.owner?.fullName) {
          const ownerGroup = node.append("g")
            .attr("transform", `translate(${totalWidth},${barHeight / 2 - 12})`);

          // Add owner avatar
          const clipId = `clip-${Math.random().toString(36).substr(2, 9)}`;
          
          // Create a placeholder for the owner avatar
          const avatarGroup = ownerGroup.append("g")
            .attr("class", "owner-avatar-placeholder")
            .attr("transform", `translate(${8}, ${8})`);
            
            // Add a default circle as placeholder
            avatarGroup.append("circle")
              .attr("r", 8)
              .attr("fill", "#E2E8F0")
              .attr("stroke", "#94A3B8")
              .attr("stroke-width", 1);

            avatarGroup.append("text")
              .attr("dy", "0.31em")
              .attr("text-anchor", "middle")
              .attr("fill", "#475569")
              .attr("font-size", "10px")
              .text(d.data.owner.fullName[0]);
            
            // Asynchronously load the photo without blocking
            if (d.data.owner.azureId) {
              employeesApi.getEmployeePhoto(d.data.owner.azureId)
                .then(photoUrl => {
                  if (photoUrl) {
                    // Remove the placeholder
                    avatarGroup.selectAll("*").remove();
                    
                    // Add clip path to owner group if it doesn't exist
                    if (ownerGroup.select(`#${clipId}`).empty()) {
                      ownerGroup.append("defs")
                        .append("clipPath")
                        .attr("id", clipId)
                        .append("circle")
                        .attr("r", 8);
                    }
                    
                    // Add the image
                    avatarGroup.append("image")
                      .attr("x", -8)
                      .attr("y", -8)
                      .attr("width", 16)
                      .attr("height", 16)
                      .attr("clip-path", `url(#${clipId})`)
                      .attr("preserveAspectRatio", "xMidYMid slice")
                      .attr("xlink:href", photoUrl);
                  }
                })
                .catch(error => {
                  console.error('Error loading owner photo:', error);
                  // Placeholder already rendered, no need to do anything
                });
            }

          totalWidth += 32;

          // Add owner name
          const ownerText = node.append("text")
            .attr("x", totalWidth)
            .attr("y", barHeight / 2)
            .attr("dy", "0.31em")
            .attr("text-anchor", "start")
            .attr("fill", "#6B7280")
            .text(d.data.owner.fullName);

          totalWidth += ownerText.node().getComputedTextLength() + 24;
        }

        // ADR-120: a unit with an end date is history — no new children, no move, no
        // delete; edit and owner history open read-only; it can be reactivated.
        const hasEndDate = Boolean(d.data.endDate);

        // Add action buttons
        // Add button
        const addButton = node.append("g")
          .attr("class", "add-subteam-button")
          .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
          .style("cursor", "pointer")
          .style("opacity", 0)
          .style("display", hasEndDate ? "none" : null)
          .on("click", (event) => {
            event.stopPropagation();
            resetForm();
            setSelectedBusinessUnit(d.data);
            setOpenModal(true);
          });

        addButton.append("circle")
          .attr("r", 7)
          .attr("fill", "#2563EB")
          .attr("stroke", "none");

        addButton.append("text")
          .attr("dy", "0.31em")
          .attr("text-anchor", "middle")
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text("+");

        totalWidth += hasEndDate ? 0 : 25;

        // History button for root node (cluster manager history)
        if (d.depth === 0) {
          const historyButton = node.append("g")
            .attr("class", "history-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .on("click", (event) => {
              event.stopPropagation();
              setHistoryModal({ open: true, entityId: clusterId, entityName: d.data.name, entityType: MANAGER_HISTORY_ENTITY_TYPES.CLUSTER });
            });

          historyButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#8B5CF6")
            .attr("stroke", "none");

          historyButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text("h");

          totalWidth += 25;
        }

        // Add edit and delete buttons for non-root business units
        if (d.depth !== 0) {
          // Edit button
          const editButton = node.append("g")
            .attr("class", "edit-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .on("click", (event) => {
              event.stopPropagation();
              setEditModal({ open: true, businessUnit: d.data, readOnly: hasEndDate });
            });

          editButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#F59E0B")
            .attr("stroke", "none");

          editButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text("e");

          totalWidth += 25;

          // Add move button
          const moveButton = node.append("g")
            .attr("class", "move-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .style("display", d.depth === 0 || hasEndDate ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              setMoveModal({ open: true, businessUnit: d.data });
            });

          moveButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#0EA5E9")
            .attr("stroke", "none");

          moveButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .text("m");

          totalWidth += hasEndDate ? 0 : 25;

          // Delete button
          const deleteButton = node.append("g")
            .attr("class", "delete-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .style("display", hasEndDate ? "none" : null)
            .on("click", (event) => {
              event.stopPropagation();
              handleDeleteBusinessUnit(d.data);
            });

          deleteButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#DC2626")
            .attr("stroke", "none");

          deleteButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "12px")
            .text("×");

          totalWidth += hasEndDate ? 0 : 25;

          // ADR-120: end (operating unit) / reactivate (unit with an end date)
          const lifecycleButton = node.append("g")
            .attr("class", hasEndDate ? "reactivate-button" : "end-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .on("click", (event) => {
              event.stopPropagation();
              setLifecycleModal({ open: true, mode: hasEndDate ? 'reactivate' : 'end', businessUnit: d.data });
            });

          lifecycleButton.append("title").text(hasEndDate ? "Reactivate business unit" : "End business unit");

          lifecycleButton.append("circle")
            .attr("r", 7)
            .attr("fill", hasEndDate ? "#16A34A" : "#64748B")
            .attr("stroke", "none");

          lifecycleButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text(hasEndDate ? "↺" : "■");

          totalWidth += 25;

          // History button for business unit (owner history)
          const historyButton = node.append("g")
            .attr("class", "history-button")
            .attr("transform", `translate(${totalWidth},${barHeight / 2})`)
            .style("cursor", "pointer")
            .style("opacity", 0)
            .on("click", (event) => {
              event.stopPropagation();
              setHistoryModal({ open: true, entityId: d.data.id, entityName: d.data.name, entityType: MANAGER_HISTORY_ENTITY_TYPES.BUSINESS_UNIT, readOnly: hasEndDate });
            });

          historyButton.append("circle")
            .attr("r", 7)
            .attr("fill", "#8B5CF6")
            .attr("stroke", "none");

          historyButton.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "white")
            .attr("font-size", "11px")
            .attr("font-weight", "bold")
            .text("h");

          totalWidth += 25;
        }
      });

      const nodeUpdate = node.merge(nodeEnter)
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${d.y})`)
        .style("opacity", 1);

      node.select(".button-group text")
        .text(d => d.children ? "−" : "+");

      nodeUpdate.select(".add-subteam-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });
      
      nodeUpdate.select(".edit-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });

      nodeUpdate.select(".move-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") && d.depth !== 0 ? 1 : 0;
        });

      nodeUpdate.select(".delete-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") && d.depth !== 0 ? 1 : 0;
        });

      nodeUpdate.select(".history-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") ? 1 : 0;
        });

      // ADR-120 (same shape as the siblings above — transition.select propagates data,
      // transition.selectAll does not)
      nodeUpdate.select(".end-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") && d.depth !== 0 ? 1 : 0;
        });

      nodeUpdate.select(".reactivate-button")
        .style("opacity", function(d) {
          return d3.select(this.parentNode).classed("selected") && d.depth !== 0 ? 1 : 0;
        });

      const nodeExit = node.exit()
        .transition()
        .duration(300)
        .attr("transform", d => `translate(0,${source.y})`)
        .style("opacity", 0)
        .remove();

      nodes.forEach(d => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    updateTree(root);

  }, [activeScope, viewOnly, initialBusinessUnit, readOnly, currentEmployeeId]);

  if (isLoading) {
    return (
      <FormContainer>
        <LoadingSpinner message="Loading business units..." />
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Content>
        {/* ADR-120: ended units are hidden by default. The toggle is for the
            configuration tab; a picker (e.g. scheduling a feedback session) is
            choosing a unit to work with now, so it offers operating units only. */}
        {!hideInactiveToggle && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
              label={<Typography variant="body2" color="text.secondary">Show inactive units</Typography>}
            />
          </Box>
        )}
        <Box 
          sx={{ 
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            p: 2,
          }}
        >
          <svg 
            ref={svgRef} 
            style={{ 
              width: '100%', 
              minWidth: '800px', 
              minHeight: businessUnits && businessUnits.childBus && businessUnits.childBus.length > 0 ? '600px' : '100px'
            }}
          />
        </Box>

        {businessUnits && (!businessUnits.childBus || businessUnits.childBus.length === 0) && (
          <Grid item xs={12}>
            <EmptyState
              title="No Business Units Found"
              description="Click on the cluster in the tree and use the '+' button to add business units or sub-business units to this cluster."
            />
          </Grid>
        )}
      </Content>

      <Modal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedBusinessUnit(null);
          resetForm();
        }}
        onSubmit={handleAddBusinessUnit}
        title={selectedBusinessUnit ? `Add Sub-business Unit to ${selectedBusinessUnit.name}` : 'Add New Business Unit'}
        submitText="Add Business Unit"
        submitDisabled={!formData.name || !formData.assignmentModel}
      >
        {renderBusinessUnitForm()}
      </Modal>

      <Modal
        isOpen={editModal.open}
        onClose={() => {
          setEditModal({ open: false, businessUnit: null, readOnly: false });
          resetForm();
        }}
        onSubmit={handleEditBusinessUnit}
        title={editModal.readOnly ? 'Business Unit (inactive)' : 'Edit Business Unit'}
        submitText="Update Business Unit"
        submitDisabled={!formData.name || !formData.assignmentModel}
        hideSubmit={editModal.readOnly}
        cancelText={editModal.readOnly ? 'Close' : 'Cancel'}
      >
        {editModal.readOnly && (
          <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
            Ended {formatDateOnly(editModal.businessUnit?.endDate)}
            {editModal.businessUnit?.endReason ? ` — ${editModal.businessUnit.endReason}` : ''}. Reactivate the unit to edit it.
          </Typography>
        )}
        {renderBusinessUnitForm()}
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, businessUnit: null })}
        onConfirm={confirmDelete}
        title="Delete Business Unit"
        message={`Are you sure you want to delete ${deleteModal.businessUnit?.name}? This will also delete all child business units.`}
        actionButtonText="Delete"
      />

      {/* ADR-118: Move Business Unit (within or across Clusters) */}
      <MoveBusinessUnitModal
        isOpen={moveModal.open}
        businessUnit={moveModal.businessUnit}
        clusterId={clusterId}
        clusterName={businessUnits?.name}
        onClose={() => setMoveModal({ open: false, businessUnit: null })}
        onMoved={handleMoved}
      />

      {/* ADR-120: End / Reactivate Business Unit */}
      <EndBusinessUnitModal
        isOpen={lifecycleModal.open}
        mode={lifecycleModal.mode}
        businessUnit={lifecycleModal.businessUnit}
        onClose={() => setLifecycleModal({ open: false, mode: 'end', businessUnit: null })}
        onDone={handleLifecycleDone}
      />

      <ManagerHistoryModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, entityId: null, entityName: '', entityType: null, readOnly: false })}
        readOnly={historyModal.readOnly}
        entityId={historyModal.entityId}
        entityName={historyModal.entityName}
        entityType={historyModal.entityType}
        onUpdate={fetchBusinessUnits}
        onCurrentManagerChange={historyModal.entityType === MANAGER_HISTORY_ENTITY_TYPES.CLUSTER ? onClusterManagerChange : undefined}
      />
    </FormContainer>
  );
};

export default BusinessUnitsTab; 